import { Component } from '@theme/component';
import { debounce, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@theme/dialog';

const RECENT_SEARCHES_STORAGE_KEY = 'horizon-recent-searches';
const RECENT_SEARCHES_LIMIT = 6;

class RecentSearchesStore {
  static #normalizeItem(item) {
    if (typeof item === 'string') {
      const term = item.trim();
      return term ? { term, image: '' } : null;
    }

    if (item && typeof item === 'object' && typeof item.term === 'string') {
      const term = item.term.trim();
      if (!term) return null;

      return {
        term,
        image: typeof item.image === 'string' ? item.image : '',
      };
    }

    return null;
  }

  static isSupported() {
    try {
      window.localStorage.setItem('__predictive_search_test__', '1');
      window.localStorage.removeItem('__predictive_search_test__');
      return true;
    } catch {
      return false;
    }
  }

  static getAll() {
    if (!this.isSupported()) return [];

    try {
      const parsed = JSON.parse(window.localStorage.getItem(RECENT_SEARCHES_STORAGE_KEY) || '[]');

      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((item) => this.#normalizeItem(item))
        .filter(Boolean)
        .slice(0, RECENT_SEARCHES_LIMIT);
    } catch {
      return [];
    }
  }

  static saveAll(terms) {
    if (!this.isSupported()) return;

    window.localStorage.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(terms.slice(0, RECENT_SEARCHES_LIMIT)));
  }

  static add(term, image = '') {
    const normalizedTerm = term.trim();
    if (!normalizedTerm) return;

    const normalizedLowercase = normalizedTerm.toLowerCase();
    const nextTerms = this.getAll().filter((item) => item.term.toLowerCase() !== normalizedLowercase);
    const existingItem = this.getAll().find((item) => item.term.toLowerCase() === normalizedLowercase);

    nextTerms.unshift({
      term: normalizedTerm,
      image: image || existingItem?.image || '',
    });

    this.saveAll(nextTerms);
  }

  static remove(term) {
    const normalizedLowercase = term.trim().toLowerCase();
    const nextTerms = this.getAll().filter((item) => item.term.toLowerCase() !== normalizedLowercase);

    this.saveAll(nextTerms);
  }

  static clear() {
    if (!this.isSupported()) return;
    window.localStorage.removeItem(RECENT_SEARCHES_STORAGE_KEY);
  }
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form - The search form element.
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['form', 'searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  #isRecentSearchesEditing = false;
  #isRecentSearchSubmit = false;
  #focusSearchInputOnDialogOpen = false;

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * @type {string}
   */
  #lastRenderedSearchTerm = '';

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.refs.searchInput.readOnly = Boolean(dialog && this.#isTouchLikeDevice());

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleComponentClick, { signal });
    }

    this.refs.form.addEventListener('submit', this.#handleFormSubmit, { signal });
    this.refs.searchInput.addEventListener('pointerdown', this.#allowSearchInputKeyboard, { signal });
    this.refs.searchInput.addEventListener('touchstart', this.#allowSearchInputKeyboard, { signal, passive: true });
    this.refs.predictiveSearchResults.addEventListener('scroll', this.#handleDiscoveryScroll, {
      signal,
      passive: true,
      capture: true,
    });
    this.refs.predictiveSearchResults.addEventListener('touchmove', this.#handleDiscoveryScroll, {
      signal,
      passive: true,
      capture: true,
    });
    this.refs.predictiveSearchResults.addEventListener('wheel', this.#handleDiscoveryScroll, {
      signal,
      passive: true,
      capture: true,
    });
    window.addEventListener('storage', this.#handleStorageUpdate, { signal });

    onDocumentLoaded(() => {
      this.#renderRecentSearches();
      this.resetSearch(false);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  /**
   * Handles clicks within the predictive search modal.
   * @param {MouseEvent} event - The mouse event
   */
  #handleComponentClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const predictiveQueryLink = target.closest('[data-predictive-query-term]');
    if (predictiveQueryLink instanceof HTMLAnchorElement) {
      const term = predictiveQueryLink.dataset.predictiveQueryTerm?.trim();
      if (term) {
        RecentSearchesStore.add(term);
      }
      return;
    }

    const predictiveResourceLink = target.closest(
      '.predictive-search-results__card--product .resource-card__link, .predictive-search-results__card .resource-card__link'
    );
    if (predictiveResourceLink instanceof HTMLAnchorElement) {
      const submittedTerm = this.refs.searchInput.value.trim();
      if (submittedTerm) {
        RecentSearchesStore.add(submittedTerm, this.#getMatchedResourceImageFromCard(predictiveResourceLink));
      }
      return;
    }

    const removeRecentSearchButton = target.closest('[data-recent-search-remove]');
    if (removeRecentSearchButton instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();

      const term = removeRecentSearchButton.dataset.recentSearchRemove;
      if (!term) return;

      RecentSearchesStore.remove(term);
      this.#renderRecentSearches();
      return;
    }

    const recentSearchButton = target.closest('[data-recent-search-term]');
    if (recentSearchButton instanceof HTMLElement) {
      event.preventDefault();
      if (this.#isRecentSearchesEditing) return;

      const term = recentSearchButton.dataset.recentSearchTerm;
      if (!term) return;

      this.#isRecentSearchSubmit = true;
      this.refs.searchInput.value = term;
      this.#showResetButton();
      this.refs.form.requestSubmit();
      return;
    }

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput && !this.#isTouchLikeDevice()) {
      this.refs.searchInput.focus();
    }


    const clearAllButton = target.closest('[data-recent-search-clear]');
    if (clearAllButton) {
      event.preventDefault();
      event.stopPropagation();

      RecentSearchesStore.clear();
      this.#renderRecentSearches();
      return;
    }
  };

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.#focusSearchInputOnDialogOpen = !this.dialog?.refs.dialog.open;
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogOpen = () => {
    if (this.#isTouchLikeDevice()) {
      this.refs.searchInput.readOnly = !this.#focusSearchInputOnDialogOpen;
    } else {
      this.refs.searchInput.readOnly = false;
    }

    if (this.#focusSearchInputOnDialogOpen || !this.#isTouchLikeDevice()) {
      this.#allowSearchInputKeyboard();
      this.refs.searchInput.focus({ preventScroll: true });
      this.#focusSearchInputOnDialogOpen = false;
    }

    requestAnimationFrame(() => {
      this.#renderRecentSearches();
    });
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#isRecentSearchesEditing = false;
    this.#focusSearchInputOnDialogOpen = false;
    this.refs.searchInput.readOnly = this.#isTouchLikeDevice();
    this.#resetSearch();
  };

  #handleFormSubmit = () => {
    const submittedTerm = this.refs.searchInput.value.trim();
    if (!submittedTerm) return;

    if (this.#isRecentSearchSubmit) {
      RecentSearchesStore.add(submittedTerm);
      this.#isRecentSearchSubmit = false;
    } else {
      RecentSearchesStore.add(submittedTerm);
    }

    this.#renderRecentSearches();
  };

  #handleStorageUpdate = (event) => {
    if (event.key !== RECENT_SEARCHES_STORAGE_KEY) return;
    this.#renderRecentSearches();
  };

  #allowSearchInputKeyboard = () => {
    this.refs.searchInput.readOnly = false;
  };

  #handleDiscoveryScroll = (event) => {
    if (!this.#isTouchLikeDevice()) return;
    if (document.activeElement !== this.refs.searchInput) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!this.#isSearchResultsScrollTarget(target)) return;

    this.refs.searchInput.blur();
  };

  #isSearchResultsScrollTarget(target) {
    const searchContentSelector =
      '.predictive-search-discovery, [data-search-results], .predictive-search-results__wrapper, .predictive-search-results__list';

    return Boolean(target.closest(searchContentSelector) || target.querySelector(searchContentSelector));
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = event.shiftKey
          ? currentIndex > 0
            ? currentIndex - 1
            : totalItems - 1
          : currentIndex < totalItems - 1
            ? currentIndex + 1
            : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        }
        break;
      }
    }
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = !this.#isTouchLikeDevice()) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   */
  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch(false);
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 250);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  #createPredictiveSearchUrl(searchTerm) {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);

    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', this.dataset.resourceLimit || '6');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');

    const resourceTypes = ['product'];

    if (this.dataset.includeCollections === 'true') resourceTypes.push('collection');
    if (this.dataset.includePages === 'true') resourceTypes.push('page');
    if (this.dataset.includeArticles === 'true') resourceTypes.push('article');
    if (this.dataset.includeQueries !== 'false') resourceTypes.push('query');

    url.searchParams.set('resources[type]', resourceTypes.join(','));

    return url;
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();
    const searchUrl = this.#createPredictiveSearchUrl(searchTerm);

    this.#setLoadingState(searchTerm);

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, searchUrl);

      if (!resultsMarkup || abortController.signal.aborted) return;

      if (this.refs.searchInput.value.trim() !== searchTerm) return;

      morph(predictiveSearchResults, resultsMarkup);
      predictiveSearchResults.dataset.state = 'results';
      this.#lastRenderedSearchTerm = searchTerm;
      this.#syncInputAriaExpanded(true);
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      this.#setErrorState();
      console.error(error);
    }
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #syncInputAriaExpanded(isExpanded) {
    this.refs.searchInput.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
  }

  #isTouchLikeDevice() {
    return (
      window.matchMedia?.('(pointer: coarse)').matches ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    );
  }

  #escapeHtml(value) {
    const root = document.createElement('div');
    root.textContent = value;
    return root.innerHTML;
  }

  #setLoadingState(searchTerm) {
    const safeSearchTerm = this.#escapeHtml(searchTerm);

    this.refs.predictiveSearchResults.dataset.state = 'loading';
    this.refs.predictiveSearchResults.innerHTML = `
      <div class="predictive-search-loading" role="status" aria-live="polite">
        <div class="predictive-search-loading__content">
          <p class="predictive-search-loading__label">Searching for "${safeSearchTerm}"</p>
          <div class="predictive-search-loading__bars" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    `;
    this.#syncInputAriaExpanded(true);
  }

  #setErrorState() {
    this.refs.predictiveSearchResults.dataset.state = 'error';
    this.refs.predictiveSearchResults.innerHTML = `
      <div class="predictive-search-feedback" role="status" aria-live="polite">
        <p class="predictive-search-feedback__title">Search is temporarily unavailable.</p>
        <p class="predictive-search-feedback__text">Try again in a moment or press enter to view full search results.</p>
      </div>
    `;
    this.#syncInputAriaExpanded(true);
  }

  #getMatchedResourceImageFromCard(cardLink) {
    const resourceCard = cardLink.closest('.predictive-search-results__card');
    if (!(resourceCard instanceof HTMLElement)) return '';

    const matchedImage = resourceCard.querySelector('.resource-card__image, .resource-card__collection-image');

    if (matchedImage instanceof HTMLImageElement) {
      return matchedImage.currentSrc || matchedImage.src || '';
    }

    return '';
  }

  #renderRecentSearches() {
    const recentSearchesRoot = this.querySelector('[data-recent-searches-root]');
    const recentSearchesHeader = this.querySelector('[data-recent-searches-header]');
    const recentSearchesList = this.querySelector('[data-recent-searches-list]');
    const recentSearchesEmpty = this.querySelector('[data-recent-searches-empty]');
    const template = this.querySelector('template[data-recent-search-item-template]');

    if (
      !(recentSearchesRoot instanceof HTMLElement) ||
      !(recentSearchesHeader instanceof HTMLElement) ||
      !(recentSearchesList instanceof HTMLElement) ||
      !(recentSearchesEmpty instanceof HTMLElement) ||
      !(template instanceof HTMLTemplateElement)
    ) {
      return;
    }

    const terms = RecentSearchesStore.getAll();
    recentSearchesList.innerHTML = '';

    for (const item of terms) {
      const fragment = template.content.cloneNode(true);
      const termButton = fragment.querySelector('[data-recent-search-term]');
      const removeButton = fragment.querySelector('[data-recent-search-remove]');
      const label = fragment.querySelector('[data-recent-search-label]');
      const imageRoot = fragment.querySelector('[data-recent-search-image]');
      const fallback = fragment.querySelector('[data-recent-search-fallback]');

      if (termButton instanceof HTMLElement) {
        termButton.dataset.recentSearchTerm = item.term;
      }

      if (removeButton instanceof HTMLElement) {
        removeButton.dataset.recentSearchRemove = item.term;
        removeButton.setAttribute('aria-label', `Remove recent search ${item.term}`);
      }

      if (label instanceof HTMLElement) {
        label.textContent = item.term;
      }

      if (fallback instanceof HTMLElement) {
        fallback.textContent = item.term.charAt(0);
      }

      if (imageRoot instanceof HTMLElement && item.image) {
        imageRoot.hidden = false;
        imageRoot.innerHTML = `<img src="${this.#escapeHtml(item.image)}" alt="" loading="lazy">`;

        if (fallback instanceof HTMLElement) {
          fallback.hidden = true;
        }
      }

      recentSearchesList.append(fragment);
    }

    const hasRenderedItems = recentSearchesList.querySelector('.predictive-search-recent__item') !== null;

    if (!hasRenderedItems) {
      this.#isRecentSearchesEditing = false;
      recentSearchesRoot.hidden = true;
      recentSearchesHeader.hidden = true;
      recentSearchesEmpty.hidden = true;
      return;
    }

    recentSearchesRoot.hidden = false;
    recentSearchesHeader.hidden = false;
    recentSearchesEmpty.hidden = true;
  }

  #resetSearch = async (clearInput = true) => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    this.#lastRenderedSearchTerm = '';

    if (clearInput) {
      searchInput.value = '';
    }

    this.#isRecentSearchesEditing = false;
    this.#hideResetButton();
    this.#renderRecentSearches();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.set('section_id', emptySectionId);

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    predictiveSearchResults.dataset.state = 'idle';
    this.#syncInputAriaExpanded(false);
    this.#renderRecentSearches();
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
